import { skipToken, useSuspenseQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { BarChart } from "components/Dashboard/BarChart";
import ChartLegend from "components/Dashboard/ChartLegend";
import { NO_MATCHED_VENDOR_LABEL } from "encamp-shared/src/waste/vendorStatsFormat";
import {
  ProcessedManifestCorrectionStatsByMonthQuery,
  ProcessedManifestCorrectionStatsByVendorQuery,
} from "generated-graphql/graphql";
import { useQueryParams } from "hooks/useQueryParams";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";

import {
  PROCESSED_MANIFEST_CORRECTION_STATS_BY_MONTH_QUERY,
  PROCESSED_MANIFEST_CORRECTION_STATS_BY_VENDOR_QUERY,
} from "./api";
import {
  buildMonthlyComplianceDataset,
  COMPLIANCE_COLORS,
  formatPercent,
} from "./correctionCompliance";
import {
  correctionDataEmptyMessage,
  formatDays,
  monthShippedDateRange,
} from "./correctionTracking";
import { upsertOmnisearchTerm } from "./omnisearch";

// The two views the parent can ask this panel to render. The parent owns the
// dropdown UI; this component just renders whichever view is active.
export const VENDOR_BREAKDOWN_VIEWS = {
  byVendor: "By Vendor",
  byMonth: "By Monthly Trends",
} as const;
export type VendorBreakdownView = keyof typeof VENDOR_BREAKDOWN_VIEWS;

interface Props {
  shippedYear: number;
  view: VendorBreakdownView;
  skip?: boolean;
  // When set, the vendor table highlights this row and the monthly chart
  // overlays this vendor's per-month bars on top of the all-vendor totals.
  selectedVendor?: string | null;
  onSelectVendor?: (vendor: string | null) => void;
}

export function VendorBreakdownTile({
  shippedYear,
  view,
  skip = false,
  selectedVendor = null,
  onSelectVendor,
}: Props) {
  const { tenantId } = useTenant();
  const { setFilters } = useQueryParams<{ omnisearch?: string }>();

  // All queries are Suspense-based: parent Suspense boundaries own the
  // loading fallback, and startTransition keeps existing data on screen
  // while new data loads after a vendor selection.
  const vendorQuery = useSuspenseQuery(
    PROCESSED_MANIFEST_CORRECTION_STATS_BY_VENDOR_QUERY,
    skip || !tenantId || view !== "byVendor"
      ? skipToken
      : { variables: { tenantId, shippedYear }, errorPolicy: "all" }
  );
  // All-vendor monthly totals — the "background" the per-vendor overlay sits on.
  const monthQuery = useSuspenseQuery(
    PROCESSED_MANIFEST_CORRECTION_STATS_BY_MONTH_QUERY,
    skip || !tenantId || view !== "byMonth"
      ? skipToken
      : { variables: { tenantId, shippedYear, vendorSource: null }, errorPolicy: "all" }
  );
  // Per-vendor monthly stats — only fetched when a vendor is selected.
  const vendorMonthQuery = useSuspenseQuery(
    PROCESSED_MANIFEST_CORRECTION_STATS_BY_MONTH_QUERY,
    skip || !tenantId || view !== "byMonth" || !selectedVendor
      ? skipToken
      : {
          variables: { tenantId, shippedYear, vendorSource: selectedVendor },
          errorPolicy: "all",
        }
  );

  const handleVendorClick = (vendor: string) => {
    // Existing behaviour: narrow the manifest grid to this vendor.
    setFilters((prev) => ({
      ...prev,
      omnisearch: upsertOmnisearchTerm(prev.omnisearch ?? "", "vendor", vendor),
    }));
    // New behaviour: toggle the vendor selection that drives the chart overlay.
    onSelectVendor?.(vendor === selectedVendor ? null : vendor);
  };

  const filterByMonth = (month: number) => {
    const range = monthShippedDateRange(shippedYear, month);
    setFilters((prev) => ({
      ...prev,
      omnisearch: upsertOmnisearchTerm(prev.omnisearch ?? "", "shippedDate", range),
    }));
  };

  return (
    <>
      {view === "byVendor" && (
        <VendorComplianceTable
          shippedYear={shippedYear}
          error={vendorQuery.error}
          data={vendorQuery.data}
          selectedVendor={selectedVendor}
          onVendorClick={handleVendorClick}
        />
      )}
      {view === "byMonth" && (
        <MonthlyComplianceBars
          shippedYear={shippedYear}
          error={monthQuery.error}
          data={monthQuery.data}
          vendorData={vendorMonthQuery.data}
          selectedVendor={selectedVendor}
          onMonthClick={filterByMonth}
        />
      )}
    </>
  );
}

interface VendorComplianceTableProps {
  shippedYear: number;
  error: unknown;
  data: ProcessedManifestCorrectionStatsByVendorQuery | undefined;
  selectedVendor: string | null;
  onVendorClick: (vendor: string) => void;
}

function VendorComplianceTable({
  shippedYear,
  error,
  data,
  selectedVendor,
  onVendorClick,
}: VendorComplianceTableProps) {
  if (error) {
    return <Typography color="error">Couldn't load vendor compliance</Typography>;
  }

  const rows = data?.processedManifestCorrectionStatsByVendor ?? [];
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {correctionDataEmptyMessage(shippedYear)}
      </Typography>
    );
  }

  return (
    // Tenants with many vendors can produce a long list; cap the table at
    // roughly six rows and scroll internally (sticky header) instead of
    // letting it eat the page's vertical space.
    <TableContainer
      sx={{
        maxHeight: "16rem",
        // stickyHeader cells default to background.default (grey); match
        // the tile's paper background instead.
        "& .MuiTableCell-stickyHeader": { backgroundColor: "background.paper" },
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Vendor</TableCell>
            <TableCell align="right">Total</TableCell>
            <TableCell align="right">Corrected ≤30d</TableCell>
            <TableCell align="right">% Compliant</TableCell>
            <TableCell align="right">Avg days</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <VendorTableRow
              key={row.vendor ?? `__unmatched-${index}`}
              row={row}
              selected={row.vendor !== null && row.vendor === selectedVendor}
              onVendorClick={onVendorClick}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

type VendorRow =
  ProcessedManifestCorrectionStatsByVendorQuery["processedManifestCorrectionStatsByVendor"][number];

function VendorTableRow({
  row,
  selected,
  onVendorClick,
}: {
  row: VendorRow;
  selected: boolean;
  onVendorClick: (vendor: string) => void;
}) {
  const theme = useTheme();
  const vendor = row.vendor;
  const clickable = vendor !== null && vendor !== undefined;

  // Selected state: MUI's `selected` adds a subtle background tint; the left
  // border accent + bold vendor name make it unmistakable at a glance.
  const selectedSx = selected
    ? {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        "& > *:first-of-type": { fontWeight: 600 },
      }
    : {
        borderLeft: "4px solid transparent",
      };

  return (
    <TableRow
      hover={clickable}
      selected={selected}
      onClick={clickable ? () => onVendorClick(vendor) : undefined}
      sx={{
        ...selectedSx,
        ...(clickable ? { cursor: "pointer" } : {}),
      }}
    >
      <TableCell>
        {clickable ? (
          vendor
        ) : (
          <Typography variant="body2" color="text.secondary" component="span">
            {NO_MATCHED_VENDOR_LABEL}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">{row.total}</TableCell>
      <TableCell align="right">{row.corrected30}</TableCell>
      <TableCell align="right">{formatPercent(row.corrected30, row.total)}</TableCell>
      <TableCell align="right">{formatDays(row.avgDaysToCorrect)}</TableCell>
    </TableRow>
  );
}

interface MonthlyComplianceBarsProps {
  shippedYear: number;
  error: unknown;
  data: ProcessedManifestCorrectionStatsByMonthQuery | undefined;
  vendorData?: ProcessedManifestCorrectionStatsByMonthQuery | undefined;
  selectedVendor: string | null;
  onMonthClick: (month: number) => void;
}

function MonthlyComplianceBars({
  shippedYear,
  error,
  data,
  vendorData,
  selectedVendor,
  onMonthClick,
}: MonthlyComplianceBarsProps) {
  const theme = useTheme();
  // Fill every visible month (Jan through the last elapsed month) so the
  // axis has no gaps; sparse axes hide the shape of the year. When a vendor
  // is selected, the dataset carries both the all-vendor totals and the
  // per-vendor breakdown so the chart can render the gray "other vendors"
  // remainder beneath the colored vendor stack.
  // Same three dataKeys (`corrected`, `uncorrected`, `other`) regardless of
  // vendor selection — keeps the chart's series structure stable so MUI
  // x-charts doesn't re-layout when a vendor is picked, only the values shift.
  // When no vendor is selected, `other` is zero everywhere and we hide its
  // legend item.
  const dataset = useMemo(
    () =>
      buildMonthlyComplianceDataset({
        summaryRows: data?.processedManifestCorrectionStatsByMonth ?? [],
        vendorRows: vendorData?.processedManifestCorrectionStatsByMonth ?? [],
        selectedVendor,
        shippedYear,
      }),
    [data, vendorData, selectedVendor, shippedYear]
  );

  if (error) {
    return <Typography color="error">Couldn't load monthly compliance</Typography>;
  }

  const hasData = dataset.some((d) => d.total > 0);
  const hasOther = dataset.some((d) => d.other > 0);
  // Waste-overview palette: same green/orange family used by the donut's
  // on-time and overdue segments, so the two visualisations read as one set.
  const correctedColor = COMPLIANCE_COLORS.onTime;
  const uncorrectedColor = COMPLIANCE_COLORS.overdue;
  const otherColor = theme.palette.grey[300];

  // Two overlaid stacks rather than one three-series stack:
  //   bg — the gray month total. Always full height, never animates.
  //   fg — the focused colored portion (the selected vendor's numbers, or
  //        the all-vendor numbers when nothing is selected).
  // barGapRatio -1 on the x-axis collapses the two stacks onto the same x
  // position; series array order is z-order, so bg (first) paints behind.
  // Toggling a vendor only resizes the fg stack, revealing or covering the
  // static gray behind it — the "other vendors" remainder never springs into
  // existence or slides along the bar, the eye just sees the colored portion
  // shrink to the selected vendor's share.
  const series = [
    {
      type: "bar" as const,
      id: "other",
      dataKey: "total",
      stack: "bg",
      color: otherColor,
      // The tooltip should report the *visible* gray remainder, not the bg
      // layer's full-month value; empty string hides the row entirely when
      // no gray is showing.
      valueFormatter: (_val: number | null, context: { dataIndex: number }) => {
        const other = dataset[context.dataIndex]?.other ?? 0;
        return other > 0 ? `Other vendors (${other})` : "";
      },
    },
    {
      type: "bar" as const,
      id: "corrected",
      dataKey: "corrected",
      stack: "fg",
      // "none", not MUI's "diverging" default: diverging snaps zero-value
      // bands to the baseline, which makes a segment slide down the bar
      // while it animates to zero instead of shrinking in place.
      stackOffset: "none" as const,
      color: correctedColor,
      valueFormatter: (val: number | null) => `Corrected ≤30d (${val ?? 0})`,
    },
    {
      type: "bar" as const,
      id: "uncorrected",
      dataKey: "uncorrected",
      stack: "fg",
      stackOffset: "none" as const,
      color: uncorrectedColor,
      valueFormatter: (val: number | null) => `Not corrected ≤30d (${val ?? 0})`,
    },
  ];

  // Hide the "Other vendors" legend swatch when its values are all zero —
  // it's an "active filter" affordance that only makes sense when a vendor
  // is selected (or, more precisely, when the gray series has data to show).
  const legendItems = [
    { color: correctedColor, value: "Corrected ≤30d" },
    { color: uncorrectedColor, value: "Not corrected ≤30d" },
    ...(hasOther ? [{ color: otherColor, value: "Other vendors" }] : []),
  ];

  return (
    <Stack spacing={1.5} sx={{ position: "relative" }}>
      <ChartLegend
        config={{ direction: "row", position: "above" }}
        items={legendItems}
        sx={{ paddingLeft: 6 }}
      />
      <Box height={theme.spacing(40)}>
        <BarChart
          dataset={dataset}
          noData={!hasData}
          noDataMessage={correctionDataEmptyMessage(shippedYear)}
          tooltipTitleProperty="tooltipTitle"
          series={series}
          xAxis={[
            {
              scaleType: "band",
              dataKey: "monthLabel",
              categoryGapRatio: 0.4,
              // Fully overlap the bg/fg stack groups on the same x position
              // (the default small positive gap would render them side by side).
              barGapRatio: -1,
            },
          ]}
          yAxis={[
            {
              scaleType: "linear",
              valueFormatter: (val) => (val ? val.toString() : "0"),
            },
          ]}
          onItemClick={(_event, item) => {
            const row = dataset[item.dataIndex];
            if (row && row.total > 0) {
              onMonthClick(row.month);
            }
          }}
          // The raw summed total would double-count (bg total + fg portion);
          // read the true month total off the dataset instead.
          stackedTooltipTotalFormatter={(_total, dataIndex) =>
            `Total requests: ${dataset[dataIndex]?.total ?? 0}`
          }
        />
      </Box>
    </Stack>
  );
}
